import React, { FC, useState, useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import Link from "next/link";
import { withRouter, NextRouter } from "next/router";
import { GET_TOPPINGS } from "../../queries/Products";
import { Query } from "react-apollo";
import CheckoutContext from "checkout";
import * as Sentry from "@sentry/browser";

import getUndef from "../../lib/get-undef";
import separateNumber from "../../lib/separate-number";
import ChecboxСonfiguration from "../common/ChecboxСonfiguration";
import CountConfiguration from "../common/CountConfiguration";
import SelectionFilter from "../../components/common/SelectionFilter";
import { RED } from "../../components/styles/constants";
import { easeIn, easeOut, easeOutFast } from "../../components/styles/atoms";
import { toppings, toppingsVariables } from "../../__generated__/toppings";
import { product } from "../../__generated__/product";
import useVariants from "../hooks/use-variants";

import { GET_ADDITIONAL, GET_SAUCE } from "../../queries/Additional";
import { additionalProducts } from "../../__generated__/additionalProducts";
import { products } from "../../__generated__/products";
import Image from "../common/Image";
import { cover } from "../styles/_fit-images";
import IconTags from "../common/IconTags";
import HelmetComponent from "../layout/HelmetComponent";
import Spinner from "../common/Spinner";
import { BtnLoading } from "../common/Button";
import getMeasurementUnit from "../../lib/get-measurement-unit";
import AddAdditionsModal from "./AddAdditionsModal";
import CountConfigurationContainer from "./CountConfigurationContainer";
import { withId } from "react-yandex-metrika";
import { CityContext } from "../context/CityContext";
import { ecommerceAddToBasket } from "../../lib/ym-ecommerce";
import CrustsSelect from "./CrustsSelect";
import PizzaSauce from "./PizzaSauce";
import { getSauce } from "../../__generated__/getSauce";
import useShop from "../hooks/use-shop";

interface Props {
  data: product;
  router: NextRouter;
}

export interface Ingredient {
  id: string;
  name: string;
  amount: number;
}

export interface IngredientWithPrice extends Ingredient {
  price: number;
}

export enum Additions {
  Empty = "empty",
  PartiallyFilled = "partially filled",
  Full = "full",
}

const ProductCard: FC<Props> = ({ data, router }) => {
  const [hasToppings, setHasToppings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [crustIngredient, setCrustIngredient] = useState<IngredientWithPrice>();
  const [ingredients, setIngredients] = useState<IngredientWithPrice[]>([]);
  const [checkedImage, setCheckedImage] = useState(true);
  const [closeTooltip, setCloseTooltip] = useState(false);
  const [isAdditionsModalOpen, setIsAdditionsModalOpen] = useState(false);
  const [additionsStatus, setAdditionsStatus] = useState(Additions.Empty);
  const [imageStatus, setImageStatus] = useState("loaded");
  const [clicks, setClicks] = useState(0);
  const [sauce, setSauce] = useState<IngredientWithPrice>();
  const { primaryDomain } = useShop();

  const { cityNameForSeo, workingHours, ymAccountId } = useContext(CityContext);

  const potato =
    hasToppings &&
    getUndef(
      () =>
        data.shop.collections[0].products[0].collections.findIndex(
          (c) => c.slug === "potatoes"
        ) >= 0
    );

  // Show tooltip 1 time
  useEffect(() => {
    try {
      const isShowed = localStorage.getItem("cheeseIsShowed");
      if (isShowed) {
        return;
      }
      setCloseTooltip(true);
      localStorage.setItem("cheeseIsShowed", "1");
    } catch (error) {
      console.error(error);
    }
  }, []);

  const product = getUndef(() => data.shop.collections[0].products[0]);

  const {
    metafields,
    images,
    name,
    seo,
    variants,
    description,
    collections,
    slug,
  } = product;

  const collectionMetafields = getUndef(() =>
    collections.reduce((prev, curr) => {
      return [...prev, ...curr.metafields];
    }, [])
  );

  const ingredientsId = getUndef(
    () =>
      collectionMetafields.filter(
        ({ key, value }) => key === "additionsCollection" && !!value
      )[0].value
  );
  const additionsId = getUndef(
    () =>
      collectionMetafields.filter(
        ({ key, value }) => key === "ingredientsCollection" && !!value
      )[0].value
  );
  const crustsId = getUndef(
    () =>
      collectionMetafields.filter(
        ({ key, value }) => key === "crustsCollection" && !!value
      )[0].value
  );

  const isPizza = !!collections.find(
    (collection) => collection.slug === "pizza"
  );
  const isFries = slug === "kartofel-fri";

  const getVariantIndex = (pizzaSize = "31 см"): number => {
    if (!isPizza && !isFries) return 0;

    if (isPizza) {
      const findByPizzaSize = (pizza) => pizza.name === pizzaSize;
      const variantIndex = variants.findIndex(findByPizzaSize);

      return variantIndex === -1 ? 0 : variantIndex;
    }

    if (isFries) {
      const findByWeight = (variant) => variant.name === " 150 гр";
      const variantIndex = variants.findIndex(findByWeight);

      return variantIndex === -1 ? 0 : variantIndex;
    }
  };

  const variantIndex = getVariantIndex();

  const {
    selected: {
      id,
      name: variantName,
      price,
      priceCompareAt,
      weight,
      weightUnit,
    },
    selectVariant,
    options,
  } = useVariants(variants, variantIndex);

  const { addToCart, checkout } = useContext(CheckoutContext);

  const lineItems = getUndef(() => checkout.lineItems) || [];
  const totalQuantity = lineItems.reduce((acc, { variant, quantity }) => {
    if (variant.product.name === name) {
      acc += quantity;
    }
    return acc;
  }, 0);

  const ingredientsWithoutPrice = ingredients.map(({ id, name, amount }) => ({
    id,
    name,
    amount,
  }));

  if (crustIngredient) {
    ingredientsWithoutPrice.push({
      id: crustIngredient.id,
      name: crustIngredient.name,
      amount: crustIngredient.amount,
    });
  }

  if (sauce) {
    ingredientsWithoutPrice.push({
      id: sauce.id,
      name: sauce.name,
      amount: sauce.amount,
    });
  }

  const slugCollection = router && router.query.code;
  const withoutCheese = metafields.find(({ key }) => key === "pic");
  const kcal = metafields.find(({ key }) => key === "ккал");
  const fats = metafields.find(({ key }) => key === "жиры");
  const squirrels = metafields.find(({ key }) => key === "белки");
  const carbohydrates = metafields.find(({ key }) => key === "углеводы");

  const ingredientsPrice =
    ingredients.reduce((acc, val) => acc + val.price * val.amount, 0) +
    (crustIngredient ? crustIngredient.price : 0) +
    (sauce ? sauce.price : 0);

  const regularUrl = getUndef(() => images[0].url);
  const cheeseUrl = getUndef(() => withoutCheese.files[0].url);
  const isNoodle =
    options && options[0] && options[0].name.toLowerCase() === "лапша";

  const src = checkedImage || !cheeseUrl ? regularUrl : cheeseUrl;

  const handleClick = () => {
    setCheckedImage(!checkedImage);
    clicks < 1 && setImageStatus("loading");
    setClicks((clicks) => clicks + 1);
  };

  const handleImageLoad = () => {
    setImageStatus("loaded");
  };

  let canonicalHref = "";

  if (typeof window !== "undefined") {
    canonicalHref =
      window.location.protocol +
      "//" +
      window.location.host +
      `/catalog/${collections[0].slug}/${product.slug}`;
  }

  const hasDiscount = Boolean(
    variants.reduce((prev, { priceCompareAt }) => {
      if (priceCompareAt) {
        return (prev += priceCompareAt);
      }
      return prev;
    }, 0)
  );

  const openModal = () => {
    setIsAdditionsModalOpen(true);
  };

  const addToBasket = async () => {
    setLoading(true);

    try {
      await addToCart({
        id,
        ingredients: ingredientsWithoutPrice,
      });

      ecommerceAddToBasket({
        id,
        name,
        price,
        category: slugCollection as string,
        quantity: 1,
      });

      try {
        withId(ymAccountId)("reachGoal", "ADD-TO-CART");
      } catch (error) {
        console.error(error);
      }
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
    setLoading(false);
    setIngredients([]);
  };

  const addToBasketOrOpenModal = () => {
    potato && additionsStatus === Additions.Empty ? openModal() : addToBasket();
  };

  const onCrustSelect = (variant: IngredientWithPrice) => {
    setCrustIngredient(variant);
  };

  const onSauceChange = (variant: IngredientWithPrice) => {
    setSauce(variant);
  };

  return (
    <>
      <Wrapper>
        {price && name && (
          <HelmetComponent
            title={
              seo.title || `${name} с доставкой в ${cityNameForSeo} - МИКАFood`
            }
            description={
              seo.description ||
              `${name} ${
                price
                  ? `всего за ${separateNumber(price + ingredientsPrice)} ₽ `
                  : ""
              }. Для ваc всегда свежие и вкусные блюда от Микаfood. Бесплатная доставка от 1500 рублей. Привезем заказ за 1 час. Работаем с ${workingHours}.`
            }
            canonicalHref={canonicalHref}
          />
        )}

        <CustomImageWrappper>
          <>
            <CustomImage
              width={1060}
              height={500}
              src={src}
              afterLoad={handleImageLoad}
            />
            <SpinnerWrapper>
              {imageStatus === "loading" && <StyledSpinner />}
            </SpinnerWrapper>
          </>
        </CustomImageWrappper>
        <IconTags collections={collections} discount={hasDiscount} />
        <Link
          href={{
            pathname: "/catalog/[code]",
            query: {
              animate: "y",
            },
          }}
          as={`/catalog/${slugCollection}`}
        >
          <Close />
        </Link>
        {withoutCheese && withoutCheese.files[0] && (
          <>
            {closeTooltip && (
              <TooltipWrapper>
                <CloseTooltip onClick={() => setCloseTooltip(false)} />
                <Tooltip>Показать пиццу без сыра</Tooltip>
                <ArrowTooltip />
              </TooltipWrapper>
            )}
            <Icon onClick={handleClick} checkedImage={checkedImage}>
              <img src="../../static/icons/cheese.svg" />
            </Icon>
          </>
        )}
      </Wrapper>
      <Content>
        <Padding>
          <MainInfo>
            <LeftBlock>
              <HeadBlock>
                <Name className="h3">
                  <NameText>{name}</NameText>
                  {!!weight && Number(weight) > 0 && !isPizza && (
                    <Mass>
                      <MeasureIco />
                      {separateNumber(weight)} гр.
                    </Mass>
                  )}
                </Name>
              </HeadBlock>
              <Description className="font-big">{description}</Description>
            </LeftBlock>
            <RightBlock>
              <div>
                {priceCompareAt && (
                  <PriceCompare>
                    {separateNumber(priceCompareAt + ingredientsPrice)}&nbsp;₽
                  </PriceCompare>
                )}
                <Price>{separateNumber(price + ingredientsPrice)}&nbsp;₽</Price>
                <AmountInButton>
                  В корзине <span>{totalQuantity} шт</span>
                </AmountInButton>
              </div>
              <AddToBasket
                disabled={primaryDomain?.holydays}
                isLoading={loading}
                onClick={
                  primaryDomain?.holydays ? null : addToBasketOrOpenModal
                }
              />
            </RightBlock>
          </MainInfo>
          {options[0] && (
            <>
              <Label>
                Выберите {isNoodle ? "лапшу" : options[0].name.toLowerCase()}
              </Label>
              <CustomSelectionFilter
                minWidth={115}
                isDetail
                options={options[0].valuesWithParams.map(
                  ({ value, variant }) => ({
                    value,
                    imageUrl: getUndef(
                      () =>
                        variants.find(({ id }) => id === variant.id).image.url
                    ),
                    description: getUndef(() =>
                      Number(variant.weight) > 0 && !isPizza
                        ? `${variant.weight} ${getMeasurementUnit(weightUnit)}`
                        : ""
                    ),
                  })
                )}
                onChange={(value) => {
                  setCrustIngredient(null);
                  selectVariant(options[0].name, value);
                }}
                selectedDefault={getUndef(() =>
                  slugCollection === "pizza" ||
                  ((isPizza || isFries) &&
                    options[0].valuesWithParams[variantIndex])
                    ? options[0].valuesWithParams[variantIndex].value
                    : options[0].valuesWithParams[0].value
                )}
              />
            </>
          )}
          {!!crustsId && (
            <AdditionsBlock>
              <Query<additionalProducts>
                query={GET_ADDITIONAL}
                variables={{
                  id: crustsId,
                }}
              >
                {({ loading, error, data }) => {
                  if (error || loading) {
                    return null;
                  }

                  return (
                    <CrustsSelect
                      key={variantName}
                      data={data}
                      variantName={variantName}
                      onSelect={onCrustSelect}
                    />
                  );
                }}
              </Query>
            </AdditionsBlock>
          )}

          {isPizza && (
            <AdditionsBlock>
              <Query<getSauce> query={GET_SAUCE}>
                {({ error, loading, data }) => {
                  if (error || loading) {
                    return null;
                  }

                  return (
                    <PizzaSauce
                      key={"asdas"}
                      data={data}
                      variantName={sauce?.name}
                      onSelect={onSauceChange}
                    />
                  );
                }}
              </Query>
            </AdditionsBlock>
          )}

          {!!additionsId && (
            <AdditionsBlock>
              <Query<toppings, toppingsVariables>
                query={GET_TOPPINGS}
                variables={{
                  id: additionsId,
                }}
                onCompleted={(data) => {
                  const hasToppings = getUndef(
                    () => data.shop.collections[0].products.length > 0
                  );
                  if (hasToppings) {
                    setHasToppings(true);
                  }
                }}
              >
                {({ loading, error, data }) => {
                  if (error || loading) {
                    return null;
                  }

                  return (
                    <>
                      <AddAdditionsModal
                        open={isAdditionsModalOpen}
                        setOpen={setIsAdditionsModalOpen}
                        addToBasket={addToBasket}
                        cleanAllChecked={() => setIngredients([])}
                        additionsStatus={additionsStatus}
                        setAdditionsStatus={setAdditionsStatus}
                      >
                        <CountConfiguration
                          data={data}
                          checked={ingredients}
                          setChecked={setIngredients}
                          setAdditionsStatus={setAdditionsStatus}
                          isModal
                        />
                      </AddAdditionsModal>
                      <CountConfigurationContainer>
                        <CountConfiguration
                          data={data}
                          checked={ingredients}
                          setChecked={setIngredients}
                          setAdditionsStatus={setAdditionsStatus}
                        />
                      </CountConfigurationContainer>
                    </>
                  );
                }}
              </Query>
            </AdditionsBlock>
          )}

          {!!ingredientsId && (
            <AdditionsBlock>
              <Query<products>
                query={GET_ADDITIONAL}
                variables={{
                  id: ingredientsId,
                }}
              >
                {({ loading, error, data }) => {
                  if (error || loading) {
                    return null;
                  }

                  return (
                    <ChecboxСonfiguration
                      label="Добавить"
                      selectedIngredients={ingredients}
                      setIngredients={setIngredients}
                      allIngredients={getUndef(
                        () => data.shop.collections[0].products
                      )}
                    />
                  );
                }}
              </Query>
            </AdditionsBlock>
          )}

          {kcal && (
            <Label>
              Энергетическая ценность (на 100 {getMeasurementUnit(weightUnit)})
            </Label>
          )}
          {(kcal || squirrels || fats || carbohydrates) && (
            <Tags>
              {kcal && <Tag>{`${kcal.value} ккал.`}</Tag>}
              {squirrels && <Tag>{`Белки: ${squirrels.value} г`}</Tag>}
              {fats && <Tag>{`Жиры: ${fats.value} г`}</Tag>}
              {carbohydrates && (
                <Tag>{`Углеводы: ${carbohydrates.value} г`}</Tag>
              )}
              {isPizza && (
                <Tag>
                  Вес: {weight} {getMeasurementUnit(weightUnit)}
                </Tag>
              )}
            </Tags>
          )}
        </Padding>

        <Sticky>
          <MobileAddToBasket
            disabled={primaryDomain?.holydays}
            isLoading={loading}
            onClick={primaryDomain?.holydays ? null : addToBasketOrOpenModal}
          >
            <span>добавить в корзину</span>
            <MobilePrice>
              {separateNumber(price + ingredientsPrice)} ₽
            </MobilePrice>
          </MobileAddToBasket>
        </Sticky>
      </Content>
    </>
  );
};

const AdditionsBlock = styled.div`
  margin-bottom: 1em;
  :last-child {
    margin-bottom: 0;
  }
`;

const CustomSelectionFilter = styled(SelectionFilter)`
  margin-bottom: 35px;
`;

const Label = styled.div`
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 10px;
`;

const MobilePrice = styled.span`
  font-weight: normal;
  font-size: 24px;
`;

const AmountInButton = styled.span`
  font-size: 14px;
  color: #8f8f8f;

  span {
    color: #000;
    margin-left: 5px;
    font-weight: bold;
  }
`;

const Price = styled.span`
  font-size: 44px;
  line-height: 0.9;
  white-space: nowrap;
`;

const PriceCompare = styled.span`
  display: block;
  font-size: 18px;
  color: rgba(0, 0, 0, 0.25);
  text-decoration: line-through;
`;

const RightBlock = styled.section`
  display: flex;
  min-width: 200px;
  align-items: center;
  flex-shrink: 0;
  text-align: right;
  margin-top: -6px;

  div {
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 750px) {
    display: none;
  }
`;

const LeftBlock = styled.section`
  /* white-space: nowrap; */
  flex-grow: 1;
`;

const MainInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 35px;
`;

const TooltipWrapper = styled.span`
  @media (max-width: 550px) {
    display: none;
  }
`;

const ArrowTooltip = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  right: 113.5px;
  top: 142px;

  background: #ffffff;
  border-radius: 3px;
  transform: matrix(0.71, -0.71, 0.71, 0.71, 0, 0);
`;

const Container = styled.div`
  width: 100%;
  border-radius: 20px;
  background-color: #fff;
`;

const HeadBlock = styled.div`
  display: flex;
  align-items: flex-end;

  @media (max-width: 550px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CustomImageWrappper = styled.div`
  z-index: -1;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: #e1e2e7;
  border-radius: 20px 20px 0 0;
`;

const CustomImage = styled(Image)`
  && {
    position: absolute;
    left: 0;
    top: 0;
    border-radius: 20px 20px 0 0;
    right: 0;
    bottom: 0;
    z-index: -1;
    display: block;
    width: 100%;
    height: 100%;
    ${cover};
    transition: filter 0.125s ease-out;

    @media (max-width: 1050px) {
      border-radius: 0;
    }
  }
`;

const Wrapper = styled.div`
  z-index: 1;
  height: 500px;
  position: relative;
  border-radius: 20px 20px 0 0;
  transition: 0.3s;
  user-select: none;
  overflow: hidden;

  @media (max-width: 1050px) {
    border-radius: 0;
  }

  @media (max-width: 550px) {
    height: 300px;
  }

  &:after {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: block;
    height: 130px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.0001) 0%,
      rgba(255, 255, 255, 0.978487) 79.19%
    );
    content: "";
  }
`;

const MeasureIco = styled.span`
  display: inline-block;
  width: 11px;
  height: 11px;
  margin-right: 7px;
  transform: translateY(1px);
  background: url("/static/icons/measure-black.svg") no-repeat center;
  vertical-align: baseline;
  background-size: contain;
`;

interface IconProps {
  checkedImage: boolean;
}

const Close = styled.a`
  position: absolute;
  right: 25px;
  top: 21px;
  width: 70px;
  height: 70px;
  border-radius: 50px;
  background: #fff url("/static/icons/close-big.svg") no-repeat center;

  @media (max-width: 550px) {
    width: 40px;
    height: 40px;
    right: 15px;
  }

  /* &:before,
  &:after {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    display: block;
    width: 25px;
    height: 2px;
    background: #000;
    margin: auto;
    content: "";

    @media (max-width: 550px) {
      width: 15px;
    }
  } */

  &:after {
    transform: rotate(90deg);
  }
`;

const Icon = styled.div<IconProps>`
  position: absolute;
  right: 25px;
  top: 110px;
  width: 70px;
  height: 70px;
  border-radius: 50px;
  background: ${({ checkedImage }) =>
    checkedImage ? "rgba(255, 220, 0, 0.956125)" : "#fff"};
  box-shadow: 0px 6px 22px
    ${({ checkedImage }) =>
      checkedImage ? "rgba(205, 179, 12, 0.706097)" : "#d2c6c6"};
  cursor: pointer;

  @media (max-width: 550px) {
    width: 40px;
    height: 40px;
    right: 15px;
    top: 75px;
  }

  img {
    position: absolute;
    right: 22px;
    top: 22px;

    @media (max-width: 550px) {
      width: 20px;
      right: 10px;
      top: 10px;
    }
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  padding-bottom: 20px;

  @media (max-width: 749px) {
    margin-bottom: -72px;
  }
`;

const Padding = styled.div`
  padding: 0 20px;
`;

const Name = styled.h1`
  margin: 0;
  padding-right: 10px;
  line-height: 1;
`;

const NameText = styled.span`
  margin-right: 10px;
`;

const Mass = styled.span`
  font-size: 12px;
  margin: 5px 0 0 0;
  white-space: nowrap;
  vertical-align: middle;
`;

const Description = styled.p`
  line-height: 27px;
  margin-bottom: 20px;
  margin-right: 20px;
`;

const CloseTooltip = styled.span`
  position: absolute;
  width: 15px;
  height: 15px;
  right: 292px;
  top: 140px;
  border-radius: 50px;
  transform: rotate(45deg);
  z-index: 1;
  cursor: pointer;

  &:before,
  &:after {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    display: block;
    width: 10px;
    height: 1px;
    background: #000;
    margin: auto;
    content: "";
  }

  &:after {
    transform: rotate(90deg);
  }
`;

const Tooltip = styled.div`
  background: #fff;
  padding: 8px;
  border-radius: 5px;
  font-size: 13px;
  position: absolute;
  right: 120px;
  top: 130px;
  padding-left: 27px;
  box-shadow: 0px 6px 22px rgba(0, 0, 0, 0.2);
`;

interface IAddToBasket {
  isLoading: boolean;
  disabled?: boolean;
}
const AddToBasket = styled.div<IAddToBasket>`
  border-radius: 10px;
  margin-left: 10px;
  ${easeIn};

  &:before {
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    display: block;
    height: 80px;
    width: 80px;
    border-radius: inherit;
    background: ${({ disabled }) => (disabled ? "#BDBDBD" : RED)}
      url("/static/icons/plus.svg") no-repeat center;
    background-size: 25px;
    box-shadow: ${({ disabled }) =>
      disabled ? "none" : "0px 6px 12px rgba(230, 25, 23, 0.4)"};
    transition: inherit;
    content: "";
  }

  &:hover {
    ${easeOut};
    &:before {
      box-shadow: ${({ disabled }) =>
        disabled ? "none" : "0px 6px 12px rgba(230, 25, 23, 0.5)"};
      transform: ${({ disabled }) =>
        disabled ? "none" : "translate3d(0, -3px, 0)"};
    }
  }

  &:active {
    ${easeOutFast};
    &:before {
      box-shadow: ${({ disabled }) =>
        disabled ? "none" : "0px 6px 12px rgba(230, 25, 23, 0.3)"};
      transform: ${({ disabled }) =>
        disabled ? "none" : "translate3d(0, 0, 0)"};
    }
  }

  ${({ isLoading }) =>
    isLoading
      ? `
        pointer-events: none;
        ${BtnLoading};
        &:before {
          background-image: none;
        }
      `
      : undefined};
`;

const Sticky = styled.div`
  position: sticky;
  z-index: 1;
  bottom: 0;
  left: 0;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Tag = styled.div`
  border-radius: 15px;
  display: flex;
  align-items: center;
  margin: 0 5px 16px 0;
  border: 1px solid #e8e5e5;
  color: #00000099;
  border-radius: 5px;
  font-size: 12px;
  padding: 5px 10px;
`;

interface IMobileAddToBasket {
  isLoading: boolean;
  disabled?: boolean;
}
const MobileAddToBasket = styled.div<IMobileAddToBasket>`
  display: flex;
  align-items: center;
  background: ${({ disabled }) => (disabled ? "#BDBDBD" : "#e61917")};
  height: 80px;
  font-weight: bold;
  font-size: 17px;
  text-transform: uppercase;
  color: #fff;
  margin-top: 30px;
  display: flex;
  justify-content: space-between;
  letter-spacing: 0.3px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  padding: 26px 20px;

  ${easeIn};

  &:active {
    ${easeOutFast};
    &:after {
      box-shadow: ${({ disabled }) =>
        disabled ? "none" : "0px 6px 12px rgba(230, 25, 23, 0.3)"};
      transform: ${({ disabled }) =>
        disabled ? "none" : "translate3d(0, 0, 0)"};
    }
  }

  ${({ isLoading }) =>
    isLoading
      ? `
        pointer-events: none;
        ${BtnLoading};
      `
      : undefined};

  @media (min-width: 750px) {
    display: none;
  }
`;

const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: 40%;
  z-index: 100000;

  & > .path {
    stroke: #fff;
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;

  position: relative;
  height: 100%;
`;

export default withRouter(ProductCard);
